/**
 * React Query mutation for updating notification settings
 * CRM Session A — Step 8
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, ApiError } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import type { NotificationSettings } from './useNotificationSettingsQuery';

export type UpdateNotificationSettingsInput = Partial<NotificationSettings>;

/**
 * Update user's notification settings with optimistic updates
 */
export function useUpdateNotificationSettings() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (updates: UpdateNotificationSettingsInput) => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<NotificationSettings>('/user/notification-settings', {
        method: 'PATCH',
        token,
        body: updates,
      });
    },
    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notification-settings', user?.id] });

      // Snapshot previous value
      const previousSettings = queryClient.getQueryData<NotificationSettings>(['notification-settings', user?.id]);

      // Optimistically update
      if (previousSettings) {
        queryClient.setQueryData<NotificationSettings>(['notification-settings', user?.id], {
          ...previousSettings,
          ...updates,
        });
      }

      return { previousSettings };
    },
    onError: (error, _updates, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(['notification-settings', user?.id], context.previousSettings);
      }

      const message = error instanceof ApiError ? error.message : 'Failed to update notification settings';
      toast.error(message);
    },
    onSuccess: (result) => {
      toast.success('Notification settings updated');

      // Write canonical result to cache
      queryClient.setQueryData<NotificationSettings>(['notification-settings', user?.id], result);
    },
    onSettled: () => {
      // Backstop invalidation
      queryClient.invalidateQueries({ queryKey: ['notification-settings', user?.id] });
    },
  });
}
